import React, { useState, useEffect, useCallback, useRef } from "react";
import { useStore, StoreTypes } from "context";
import styles from "./index.module.scss";
import classnames from "classnames";
import { Script } from "components/ExtendedContent/ExtendedContentContainer/layout";
import { scriptLoader } from "util/scriptLoader";
import * as types from "constants/actionTypes";
import { parseChineseOnly } from "util/zhuyin";
import {
  ScrollPositionType,
  ScrollLength,
  ScrollCoordinate,
} from "constants/scrollPositionType";
import { scrollPositionButtonBehavior } from "../ExtendedContent/ExtendedContentScrollPositionButton";
import { paginate } from "./pagination";
import { stripMathAnnotations } from "./pagination/htmlUtils";
import { EventBus, EventBusType } from "events/EventBus";
import { CanvasEvent } from "events/EventTypes";
import { SplitType } from "constants/splitType";

const pageSize = 7;

const SynopsisWindow = ({ synopsisInfo, extendedResourceStyle }) => {
  const [{ shouldSplited, splitType }] = useStore(StoreTypes.extendedContent);
  const { userAgent } = navigator;
  const listRef = useRef();
  const containerRef = useRef();
  const contentRef = useRef();
  const { list } = synopsisInfo;
  const [
    { synopsisList, synopsisTab, fontSize, currentPage, totalPages },
    extendedContentDispatch,
  ] = useStore(StoreTypes.extendedContent);
  const [{ bookId, books }] = useStore(StoreTypes.books);
  const [isChineseSecondary, setIsChineseSecondary] = useState(false);
  const [isChineseElementary, setIsChineseElementary] = useState(false);
  const book = books.find((info) => info.bookId === bookId);
  const elementaryHighGradeCheck =
    book.display_name.includes("國小國語5") ||
    book.display_name.includes("國小國語6") ||
    book.display_name.includes("國中國文"); //新增國中國文行距要縮小的需求

  // 國中國文/國小高年級需移除注音以縮小行距 (hruby line-height:1.8 → 預設)；
  // NO_SPLIT / AUTO_SPLIT / MANUAL_SPLIT 各分頁模式統一套用。
  const parseHighGrade = (value) =>
    elementaryHighGradeCheck && typeof value === "string"
      ? parseChineseOnly(value, "pirls")
      : value;

  const [{ scrollPosition }] = useStore(StoreTypes.extendedContent);
  const [currentScrollTop, setCurrentScrollTop] = useState(0);
  const [lastScrollTop, setLastScrollTop] = useState(0);
  const [pagedHtml, setPagedHtml] = useState(null);
  const currentScriptRef = useRef(null);
  const prevPageRef = useRef(currentPage);
  const prevTabRef = useRef(synopsisTab);
  const containerHeightRef = useRef(0);

  useEffect(() => {
    // scriptLoader 目前只保留無法在 handler 通用處理的業務邏輯，
    // 例如 topic 類型的「選項選擇 → 判斷答對錯 → DOM 操作」。
    // show/hide toggle 等純 UI 行為已全數交由下方的 click handler 統一處理。
    if (synopsisList[synopsisTab] && Script[extendedResourceStyle]) {
      const scriptSrc = Script[extendedResourceStyle];

      if (currentScriptRef.current) {
        const oldScript = currentScriptRef.current;
        if (oldScript && oldScript.parentNode) {
          oldScript.parentNode.removeChild(oldScript);
        }
      }

      const rafId = requestAnimationFrame(() => {
        const script = scriptLoader(scriptSrc);
        currentScriptRef.current = script;
        script.addEventListener("load", () => {
          [
            "#question ~ #answer",
            "#question #answer",
            "#question ~ #analyze",
          ].forEach((selector) => {
            const el = document.querySelector(selector);
            if (el && el.parentNode) {
              el.parentNode.replaceChild(el.cloneNode(true), el);
            }
          });
        });
      });

      return () => {
        cancelAnimationFrame(rafId);
        if (currentScriptRef.current) {
          const scriptToRemove = currentScriptRef.current;
          if (scriptToRemove && scriptToRemove.parentNode) {
            scriptToRemove.parentNode.removeChild(scriptToRemove);
          }
          currentScriptRef.current = null;
        }
      };
    }
  }, [extendedResourceStyle, synopsisList, synopsisTab, pagedHtml]);

  // 重置內容縮放到預設大小
  const resetContentScale = useCallback(() => {
    if (!containerRef.current) return;
    const content = containerRef.current;
    content.style.fontSize = "";
    content.style.transform = "none";
    content.style.transformOrigin = "";
    content.style.height = "";
    content.style.width = "100%";
  }, []);

  // 分頁處理
  useEffect(() => {
    if (!synopsisList[synopsisTab] || !containerRef.current) return;

    let cancelled = false;
    let rafId = null;

    const currentSplitType =
      (list && list[synopsisTab] && list[synopsisTab].splitType) || splitType;

    if (currentSplitType === SplitType.MANUAL_SPLIT) {
      setPagedHtml(stripMathAnnotations(parseHighGrade(synopsisList[synopsisTab]["1"])));

      const totalPages = Object.keys(synopsisList[synopsisTab]).length;
      extendedContentDispatch({
        type: types.SET_EXTENDED_CONTENT_TOTAL_PAGES,
        payload: { totalPages },
      });
      return;
    }

    const html = parseHighGrade(synopsisList[synopsisTab].html);

    if (!html) return;

    if (
      !shouldSplited ||
      (shouldSplited && currentSplitType === SplitType.NO_SPLIT)
    ) {
      setPagedHtml(stripMathAnnotations(html));
      extendedContentDispatch({
        type: types.SET_EXTENDED_CONTENT_TOTAL_PAGES,
        payload: { totalPages: 1 },
      });
      return;
    }

    resetContentScale();

    rafId = requestAnimationFrame(async () => {
      if (cancelled) return;

      // lineHeight 的計算對所有版型一致，故統一在此計算一次
      const computedStyle = window.getComputedStyle(containerRef.current);
      const lineHeight =
        parseFloat(computedStyle.lineHeight) ||
        parseFloat(computedStyle.fontSize) * 1.5;

      try {
        // 透過 registry 依 layoutType 分派到對應的 per-style 策略；
        // 未註冊的版型由 paginate 回傳單頁（行為等同舊的 fallback 分支）
        const { htmlString, totalPages: calculatedTotalPages } = await paginate(
          html,
          {
            layoutType: extendedResourceStyle,
            lineHeight,
            pageSize,
            referenceContainer: containerRef.current,
          },
        );

        if (cancelled) return;
        setPagedHtml(htmlString);
        extendedContentDispatch({
          type: types.SET_EXTENDED_CONTENT_TOTAL_PAGES,
          payload: { totalPages: calculatedTotalPages },
        });
      } catch (err) {
        // 第二道保險：分頁過程若拋錯（或某個 await 異常），
        // 不可讓 tab 切換靜默卡在舊內容——退回未分頁的整段內容並顯示為單頁。
        if (cancelled) return;
        console.error("[SynopsisWindow] 分頁失敗，退回未分頁顯示：", err);
        setPagedHtml(stripMathAnnotations(html));
        extendedContentDispatch({
          type: types.SET_EXTENDED_CONTENT_TOTAL_PAGES,
          payload: { totalPages: 1 },
        });
      }
    });

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [
    synopsisList,
    synopsisTab,
    elementaryHighGradeCheck,
    extendedContentDispatch,
    resetContentScale,
    shouldSplited,
    extendedResourceStyle,
    list,
    splitType,
  ]);

  // 更新顯示的頁面
  const updateDisplayPage = useCallback((pageIndex) => {
    if (!contentRef.current) return;

    const pageElements = contentRef.current.querySelectorAll(".synopsis-page");
    pageElements.forEach((pageEl, index) => {
      if (index === pageIndex) {
        pageEl.style.visibility = "visible";
        pageEl.style.position = "relative";
      } else {
        pageEl.style.visibility = "hidden";
        pageEl.style.position = "absolute";
      }
    });
  }, []);

  // 響應 store 中的 currentPage 變化
  useEffect(() => {
    if (pagedHtml && contentRef.current && totalPages > 0 && currentPage >= 0) {
      updateDisplayPage(currentPage);
    }
  }, [pagedHtml, currentPage, totalPages, updateDisplayPage]);

  // 抽出圖片高度限制邏輯為獨立函數
  const applyImageMaxHeight = useCallback(() => {
    if (!contentRef.current || !listRef.current) return;
    const containerH = listRef.current.clientHeight - 20;
    const maxH = containerH > 0 ? `${containerH}px` : "80vh";
    const textImgs = contentRef.current.querySelectorAll(".text img");
    textImgs.forEach((img) => {
      img.style.maxHeight = maxH;
      img.style.objectFit = "contain";
    });
  }, []);

  // 當 currentPage 變化時，保存舊頁面的畫記並載入新頁面的畫記
  useEffect(() => {
    if (!shouldSplited) return;
    if (pagedHtml && totalPages > 0 && currentPage >= 0 && synopsisTab >= 0) {
      const isTabChanged = prevTabRef.current !== synopsisTab;

      // Tab 切換時的 save 已在 onTabClickHandler 中處理，此處跳過
      // 只在同一 tab 內翻頁時才 save
      if (
        !isTabChanged &&
        prevPageRef.current !== currentPage &&
        prevPageRef.current >= 0
      ) {
        EventBus.emit({
          eventBusType: EventBusType.ExtendedContent,
          event: CanvasEvent.SplitedPageSaveCanvasEvent,
          payload: {
            currentSplitedPageIndex: prevPageRef.current,
            tabIndex: synopsisTab,
          },
        });
      }

      prevTabRef.current = synopsisTab;
      prevPageRef.current = currentPage;

      // 載入新頁面的畫記（延遲到下一個 tick，確保 save 完成）
      const timer = setTimeout(() => {
        EventBus.emit({
          eventBusType: EventBusType.ExtendedContent,
          event: CanvasEvent.SplitedPageImportJSONEvent,
          payload: {
            currentSplitedPageIndex: currentPage,
            tabIndex: synopsisTab,
          },
        });
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [currentPage, pagedHtml, totalPages, synopsisTab, shouldSplited]);

  // 調整內容縮放，使內容（文字+圖片）能完整顯示在視窗中
  const adjustContentScale = useCallback(() => {
    if (!listRef.current || !contentRef.current) return;
    const container = listRef.current;
    const content = contentRef.current;

    // 重置所有樣式，確保測量準確
    content.style.fontSize = "";
    content.style.transform = "none";
    content.style.transformOrigin = "";
    content.style.height = "";
    content.style.width = "100%";

    // 重置所有圖片的 inline 縮放樣式（稍後依容器大小重新設定寬高底盤）
    const images = content.querySelectorAll("img");
    images.forEach((img) => {
      img.style.maxWidth = "";
      img.style.maxHeight = "";
    });

    // 強制重排一次
    void container.offsetHeight;

    // 計算容器和內容高度
    const containerHeight = container.clientHeight;
    containerHeightRef.current = containerHeight;

    // 圖片寬高底盤：任何圖片都不得超出視窗寬度與可用高度。
    // 原本 adjustContentScale 只在「高度」溢出時才縮放、從不限制寬度，導致又寬又矮的
    // 表格圖在 manualSplit 下橫向破版（manualSplit 的圖片沒有被 resizeImageIfNeeded
    // 事先綁寬，又沒有可用的 CSS max-width）。在此統一補上 max-width:100% + max-height，
    // manualSplit / autoSplit 皆受惠。並讓 originalImageWidths 於套上底盤「之後」量測，
    // 使下方高度二分搜尋的縮放基準是「可容納寬」而非原生寬。
    const imageMaxHeight = containerHeight > 0 ? containerHeight - 20 : 0;
    images.forEach((img) => {
      img.style.maxWidth = "100%";
      if (imageMaxHeight > 0) img.style.maxHeight = `${imageMaxHeight}px`;
    });
    void container.offsetHeight;

    // 記錄每張圖片（已套寬高底盤後）的渲染寬度，作為高度二分搜尋的縮放基準
    const originalImageWidths = Array.from(images).map(
      (img) => img.offsetWidth,
    );

    let contentHeight = content.scrollHeight;

    // 如果內容高度超過容器高度，使用二分搜索找到最佳字體大小
    if (contentHeight > containerHeight && contentHeight > 0) {
      // 取得原始字體大小
      const computedStyle = window.getComputedStyle(content);
      const originalFontSize = parseFloat(computedStyle.fontSize);

      // 二分搜索參數
      let minFontSize = originalFontSize * 0.3; // 最小 30%
      let maxFontSize = originalFontSize; // 最大 100%
      const tolerance = 5; // 容許 5px 誤差
      const maxIterations = 20;

      let bestFontSize = minFontSize;
      let iteration = 0;

      while (maxFontSize - minFontSize > 0.5 && iteration < maxIterations) {
        const midFontSize = (minFontSize + maxFontSize) / 2;
        const scaleRatio = midFontSize / originalFontSize;

        // 應用測試字體大小
        content.style.fontSize = `${midFontSize}px`;

        // 以圖片原始渲染寬度為基準等比縮放
        images.forEach((img, i) => {
          img.style.maxWidth = `${originalImageWidths[i] * scaleRatio}px`;
        });

        // 強制重排
        void content.offsetHeight;

        // 測量調整後的實際高度
        const actualHeight = content.scrollHeight;

        if (actualHeight <= containerHeight + tolerance) {
          // 高度合適，記錄這個字體大小，嘗試更大的值
          bestFontSize = midFontSize;
          minFontSize = midFontSize;
        } else {
          // 高度太大，需要更小的字體
          maxFontSize = midFontSize;
        }

        iteration++;
      }

      // 應用最佳字體大小和圖片縮放
      const finalRatio = bestFontSize / originalFontSize;
      content.style.fontSize = `${bestFontSize}px`;
      images.forEach((img, i) => {
        img.style.maxWidth = `${originalImageWidths[i] * finalRatio}px`;
      });
    } else {
      // 內容未超過容器，保持預設字體大小
      content.style.fontSize = "";
    }
  }, []);

  // 內容渲染後的統一處理：分頁顯示 + 縮放調整
  // 取代原本分散的 updateDisplayPage effect、forceRerender effect、adjustContentScale effect
  useEffect(() => {
    if (!pagedHtml || !contentRef.current) return;

    const rafId = requestAnimationFrame(() => {
      if (shouldSplited) {
        if (totalPages > 0 && currentPage >= 0) {
          updateDisplayPage(currentPage);
          adjustContentScale();
        }
      } else {
        applyImageMaxHeight();
      }
    });

    return () => cancelAnimationFrame(rafId);
  }, [
    pagedHtml,
    currentPage,
    fontSize,
    totalPages,
    shouldSplited,
    synopsisTab,
    updateDisplayPage,
    adjustContentScale,
    applyImageMaxHeight,
  ]);

  useEffect(() => {
    let isChineseSecondary = false,
      isChineseElementary = false;
    synopsisList.forEach((item, index) => {
      if (!item) return;

      const currentSplitType =
        (list && list[index] && list[index].splitType) || splitType;

      if (currentSplitType === SplitType.MANUAL_SPLIT) {
        if (item["1"] && item["1"].includes("CHINESE SECONDARY")) {
          isChineseSecondary = true;
        } else if (item["1"] && item["1"].includes("CHINESE ELEMENTARY")) {
          isChineseElementary = true;
        }
      } else {
        if (item.html && item.html.includes("CHINESE SECONDARY")) {
          //  判斷國中國文
          isChineseSecondary = true;
        } else if (item.html && item.html.includes("CHINESE ELEMENTARY")) {
          //  判斷國小國文
          isChineseElementary = true;
        }
      }
    });
    setIsChineseSecondary(isChineseSecondary);
    setIsChineseElementary(isChineseElementary);
  }, [synopsisList, synopsisTab, splitType, list]);

  const onTabClickHandler = useCallback(
    (index) => {
      // 如果點擊的是同一個 tab，不需要處理
      if (index === synopsisTab) return;

      // 保存舊 tab 當前頁面的畫記
      if (
        pagedHtml &&
        totalPages > 0 &&
        currentPage >= 0 &&
        synopsisTab >= 0 &&
        shouldSplited
      ) {
        EventBus.emit({
          eventBusType: EventBusType.ExtendedContent,
          event: CanvasEvent.SplitedPageSaveCanvasEvent,
          payload: {
            currentSplitedPageIndex: currentPage,
            tabIndex: synopsisTab,
          },
        });
      }

      // 切換 tab 時先重置 scale 到預設大小
      resetContentScale();

      // 切換 tab
      extendedContentDispatch({
        type: types.SET_EXTENDED_CONTENT_SYNOPSIS_TAB,
        payload: {
          synopsisTab: index,
        },
      });

      if (shouldSplited) {
        // 重置到第一頁（這會觸發 currentPage 變化的 useEffect，自動載入新 tab 的畫記）
        extendedContentDispatch({
          type: types.SET_EXTENDED_CONTENT_CURRENT_PAGE,
          payload: { currentPage: 0 },
        });
      }
    },
    [
      extendedContentDispatch,
      synopsisTab,
      currentPage,
      pagedHtml,
      totalPages,
      shouldSplited,
      resetContentScale,
    ],
  );

  useEffect(() => {
    if (!synopsisList[synopsisTab]) return;
    const currentSplitType =
      (list && list[synopsisTab] && list[synopsisTab].splitType) || splitType;
    if (currentSplitType === SplitType.MANUAL_SPLIT) {
      const page = (currentPage + 1).toString();
      setPagedHtml(stripMathAnnotations(parseHighGrade(synopsisList[synopsisTab][page])));
      return;
    }
  }, [synopsisList, synopsisTab, splitType, currentPage, elementaryHighGradeCheck, list]);

  useEffect(() => {
    listRef.current && listRef.current.scrollTo(listRef.current.scrollWidth, 0);
  }, [synopsisTab]);

  useEffect(() => {
    // 分頁模式由 currentPage 控制頁面切換，不需要 scrollTop，跳過避免觸發 ClickEraseAllEvent
    if (shouldSplited) return;

    // 小視窗內容上下頁分段
    // console.log("---C類小視窗---SynopsisWindow", listRef.current);
    switch (scrollPosition) {
      case ScrollPositionType.Next: {
        setLastScrollTop(listRef.current.scrollTop);
        listRef.current.scrollTop +=
          listRef.current.offsetHeight * ScrollLength;
        setCurrentScrollTop(listRef.current.scrollTop);
        break;
      }
      case ScrollPositionType.Prev: {
        setLastScrollTop(listRef.current.scrollTop);
        listRef.current.scrollTop -=
          listRef.current.offsetHeight * ScrollLength;
        setCurrentScrollTop(listRef.current.scrollTop);
        break;
      }
      default: {
        scrollPositionButtonBehavior({
          currentScroll: currentScrollTop,
          lastScroll: lastScrollTop,
          coordinate: ScrollCoordinate.Y,
        });
        break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollPosition, shouldSplited]);

  // 文字排版，如稿紙徹底對齊文字和標點
  useEffect(() => {
    const timer = setTimeout(() => {
      // listRef 可能在 setTimeout 觸發前就已卸載（彈窗關閉、或內容容器重掛），
      // 此時 listRef.current 為 null，直接略過避免 querySelector 拋錯。
      if (!listRef.current) return;
      const isChineseOnly = listRef.current.querySelector(".chineseOnly");
      if (isChineseOnly) return;
      const ruby = listRef.current.querySelector(":not(.oval) > .az");
      const paragraphIndent = listRef.current.querySelector(".indent");
      const rubyHeight = ruby ? getComputedStyle(ruby).height : null;
      const punctuation = listRef.current.querySelectorAll(
        ":not(.circle) > sign",
      );
      const listBegin = listRef.current.querySelectorAll(".begin");
      const circleListBegin = listRef.current.querySelectorAll(".circle");
      punctuation &&
        punctuation.forEach((element) => {
          element.style.height = rubyHeight;
          // element.style.display = 'inline-flex';
          // element.style.textIndent = 'initial';
          // element.style.justifyContent = 'center';
        });
      listBegin &&
        listBegin.forEach((element) => {
          if (element.childElementCount === 0) return;
          // element.style.display = 'inline-block';
          element.style.height = rubyHeight;
          element.style.width = rubyHeight;
        });
      circleListBegin &&
        circleListBegin.forEach((element) => {
          element.style.height = rubyHeight;
          element.style.width = rubyHeight;
        });
      if (paragraphIndent)
        paragraphIndent.style.textIndent = `${
          2 * rubyHeight.replace("px", "")
        }px`;
    }, 100);
    return () => clearTimeout(timer);
  }, [fontSize, synopsisList, synopsisTab, listRef]);

  // pagedHtml 設定後手動觸發 MathJax 重新 typeset，
  // 避免 Electron 環境下 MathJax 在 DOM 內容出現前就已完成排版。
  // Step 2：typeset 完成後再做一次 adjustContentScale——否則 scale 會用
  // 「未排版的數學高度」算出錯誤比例，導致算式撐開後內容溢出/出現卷軸（NBK-1463 類）。
  // typeset 仍維持每個 pagedHtml 觸發一次（不在翻頁時重跑，避免 MathType 重複，NBK-1465）。
  useEffect(() => {
    if (pagedHtml && contentRef.current) {
      if (window.MathJax && window.MathJax.Hub) {
        window.MathJax.Hub.Queue(
          ["Typeset", window.MathJax.Hub, contentRef.current],
          () => {
            if (shouldSplited) adjustContentScale();
          },
        );
      }
    }
  }, [pagedHtml, shouldSplited, adjustContentScale]);

  // 原生事件綁定（備援模式，繞過 React 16 事件委派）
  useEffect(() => {
    const el = contentRef.current;
    // console.log('[bind] useEffect 執行, el:', el, 'pagedHtml:', !!pagedHtml);
    if (!el || !pagedHtml) return;

    const handler = (e) => {
      // console.log('[bind] click 觸發! target:', e.target.tagName, e.target.className);
      const wordAns = e.target.closest(".wordAns");
      if (wordAns) {
        // console.log('[bind] 找到 .wordAns, onclick:', wordAns.onclick);
        if (!wordAns.onclick) wordAns.classList.toggle("show");
        return;
      }

      const check = e.target.closest(".check");
      if (check) {
        if (!check.onclick) {
          const t = check.nextSibling && check.nextSibling.nextSibling;
          if (t && t.classList) t.classList.toggle("show");
        }
        return;
      }

      const ansCheck = e.target.closest(".ansCheck");
      if (ansCheck) {
        if (!ansCheck.onclick) {
          const p = ansCheck.parentNode;
          const t = p && p.nextSibling && p.nextSibling.nextSibling;
          if (t && t.classList) t.classList.toggle("show");
        }
        return;
      }

      const answers = e.target.closest(".ANSWERS");
      if (answers) {
        if (!answers.onclick) answers.classList.toggle("show");
        return;
      }

      const answer = e.target.closest("#answer");
      if (answer) {
        if (!answer.onclick) answer.classList.toggle("show");
        return;
      }

      const analyze = e.target.closest("#analyze");
      if (analyze) {
        if (!analyze.onclick) analyze.classList.toggle("show");
        return;
      }

      const fillAns = e.target.closest(".ans");
      if (fillAns && fillAns.closest(".fill")) {
        if (!fillAns.onclick) fillAns.classList.toggle("show");
        return;
      }
    };

    el.addEventListener("click", handler);
    return () => el.removeEventListener("click", handler);
  }, [pagedHtml]);

  return (
    <div
      className={classnames(
        styles.synopsisWindow,
        {
          [styles.wordSection]: extendedResourceStyle === "WordSection",
          [styles.ChineseSecondary]: isChineseSecondary,
          [styles.ChineseElementary]: isChineseElementary,
        },
        fontSize,
      )}
    >
      <div
        className={classnames(styles.tab_box, {
          [styles.mobile]:
            /Android/i.test(userAgent) || /iPhone|iPad|iPod/i.test(userAgent),
        })}
      >
        {list &&
          list.map((obj, i) => (
            <div
              className={classnames(styles.tab, {
                [styles.active]: synopsisTab === i,
              })}
              key={i}
              onClick={() => onTabClickHandler(i)}
            >
              {obj.name}
            </div>
          ))}
      </div>
      <div
        ref={listRef}
        className={classnames(styles.list, {
          noScroll: shouldSplited,
          [styles.noScroll]: shouldSplited,
        })}
      >
        <div ref={containerRef} className={styles.contentContainer}>
          {pagedHtml && (
            <div
              ref={contentRef}
              dangerouslySetInnerHTML={{
                __html: pagedHtml,
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SynopsisWindow;
